import { createAsyncThunk } from '@reduxjs/toolkit';

import sendRequest from '../../../service/sendRequest';
import consumerApi, { AUTH_SHAPE } from '../../../service/consumerApi';
import { somethingWrongNotification } from '../../notifier/notifierActions';
import { clearConsumerSession, setConsumerSession } from '../consumerReducer';
import { ASSISTANTS_CATEGORY, GENIE_EVALUATE_TYPE, PROJECT_TYPES } from '../../../constants';
import {
  cancelEvaluationByType,
  setEvalAbortController,
  updateEvaluatedGeneratedMessages,
  updateEvaluatedPredictedMessages,
} from '../../genie/genieReducer';

const GET_CONSUMER_GENIE_DATA = 'consumer/genie/GET_CONSUMER_GENIE_DATA';
const CONSUMER_GENIE_PREDICT_RESPONSE = 'consumer/genie/CONSUMER_GENIE_PREDICT_RESPONSE';
const CONSUMER_GENIE_GENERATE_ALTERNATE = 'consumer/genie/CONSUMER_GENIE_GENERATE_ALTERNATE';
const CONSUMER_GENIE_RESET_SESSION = 'consumer/genie/CONSUMER_GENIE_RESET_SESSION';
const CONSUMER_GENIE_REPLY = 'consumer/genie/CONSUMER_GENIE_REPLY';
const CONSUMER_GENIE_GENERATE_MESSAGES = 'consumer/genie/CONSUMER_GENIE_GENERATE_MESSAGES';
const EVALUATE_ALTERNATE_MESSAGES = 'consumer/genie/EVALUATE_ALTERNATE_MESSAGES';
const EVALUATE_GENERATED_MESSAGES = 'consumer/genie/EVALUATE_GENERATED_MESSAGES';
const GET_CONSUMER_GENIE_AUDIENCES = 'consumer/genie/GET_CONSUMER_GENIE_AUDIENCES';

export const getConsumerGenieData = createAsyncThunk(
  GET_CONSUMER_GENIE_DATA,
  async ({ projectName, projectPassword }, { dispatch, rejectWithValue }) => {
    const response = await sendRequest(
      () =>
        consumerApi.post(`/api/consumer/genie`, null, {
          authShape: AUTH_SHAPE.FLAT,
          consumerCredentials: { projectName, projectPassword },
        }),
      {
        onError: () => {
          dispatch(somethingWrongNotification());
          dispatch(clearConsumerSession());
          return rejectWithValue(null);
        },
      },
    );

    dispatch(
      setConsumerSession({
        projectType: PROJECT_TYPES.GENIE,
        projectName,
        projectPassword,
      }),
    );

    return response.data;
  },
);

export const predictConsumerResponse = createAsyncThunk(
  CONSUMER_GENIE_PREDICT_RESPONSE,
  async ({ message, audienceId }, { rejectWithValue }) => {
    const requestBody = {
      message: [message],
      ...(audienceId ? { audienceId } : {}),
    };

    const response = await sendRequest(
      () =>
        consumerApi.post(`/api/consumer/genie/predict/bunch`, requestBody, {
          authShape: AUTH_SHAPE.PROJECT_REQUEST,
        }),
      {
        onError: (e) => rejectWithValue(e),
      },
    );

    return response.data[0];
  },
);

export const generateConsumerAlternate = createAsyncThunk(
  CONSUMER_GENIE_GENERATE_ALTERNATE,
  async ({ message, url, audienceId }, { rejectWithValue, dispatch }) => {
    dispatch(cancelEvaluationByType(GENIE_EVALUATE_TYPE.ALTERNATE));

    const requestBody = { message, url };

    const response = await sendRequest(
      () =>
        consumerApi.post(`/api/consumer/genie/predict/messages`, requestBody, {
          authShape: AUTH_SHAPE.PROJECT_REQUEST,
        }),
      {
        onError: (e) => rejectWithValue(e),
      },
    );

    dispatch(evaluateAlternateMessagesConsumer({ values: { audienceId, url }, messages: response.data }));

    return response.data;
  },
);

export const generateConsumerGenieMessages = createAsyncThunk(
  CONSUMER_GENIE_GENERATE_MESSAGES,
  async ({ values }, { rejectWithValue, dispatch }) => {
    const { byPass, focusOn, url, channels, fileText } = values;

    const assistantId = channels[ASSISTANTS_CATEGORY.GENERATE_ALL].assistant?.id;

    if (!assistantId || !url) {
      return rejectWithValue(null);
    }

    dispatch(cancelEvaluationByType(GENIE_EVALUATE_TYPE.GENERATED));

    const requestBody = {
      assistantId,
      byPass,
      focusOn,
      url,
      fileText,
    };

    const response = await sendRequest(
      () =>
        consumerApi.post(`/api/consumer/genie/url`, requestBody, {
          authShape: AUTH_SHAPE.PROJECT_REQUEST,
        }),
      {
        onError: (e) => {
          dispatch(somethingWrongNotification());
          return rejectWithValue(e);
        },
      },
    );

    dispatch(evaluateGeneratedMessagesConsumer({ values, messages: response.data.messages }));

    return response.data;
  },
);

export const replyConsumerGenie = createAsyncThunk(
  CONSUMER_GENIE_REPLY,
  async ({ reply, assistantId, generateFormValues }, { rejectWithValue, getState, dispatch }) => {
    const storedThreadId = getState().genie.threadId;

    const requestBody = {
      reply,
      assistantId,
      threadId: storedThreadId,
    };

    const response = await sendRequest(
      () =>
        consumerApi.post(`/api/consumer/genie/reply`, requestBody, {
          authShape: AUTH_SHAPE.PROJECT_REQUEST,
        }),
      {
        onError: (e) => rejectWithValue(e),
      },
    );

    dispatch(evaluateGeneratedMessagesConsumer({ values: generateFormValues, messages: response.data.messages }));

    return response.data;
  },
);

export const resetGenieSession = createAsyncThunk(
  CONSUMER_GENIE_RESET_SESSION,
  async (_, { rejectWithValue, getState, dispatch }) => {
    const threadId = getState().genie.threadId;

    if (!threadId) {
      return rejectWithValue(null);
    }

    dispatch(cancelEvaluationByType(GENIE_EVALUATE_TYPE.ALTERNATE));
    dispatch(cancelEvaluationByType(GENIE_EVALUATE_TYPE.GENERATED));

    const response = await sendRequest(() => consumerApi.post(`/api/consumer/open-ai/reset?threadId=${threadId}`), {
      onError: (e) => rejectWithValue(e),
    });

    return response.data;
  },
);

const sendMessagesEvaluation = async ({ messages, commonData, signal }) => {
  const requestBody = {
    message: [...messages],
    ...commonData,
  };

  try {
    const response = await consumerApi.post(`/api/consumer/genie/predict/bunch`, requestBody, {
      authShape: AUTH_SHAPE.PROJECT_REQUEST,
      signal,
    });
    return response.data;
  } catch (error) {
    if (signal.aborted) throw new Error('Request cancelled');
    throw error;
  }
};

export const evaluateAlternateMessagesConsumer = createAsyncThunk(
  EVALUATE_ALTERNATE_MESSAGES,
  async ({ values, messages }, { rejectWithValue, dispatch }) => {
    const { audienceId } = values;
    const commonData = { ...(audienceId ? { audienceId } : {}) };

    const controller = new AbortController();
    dispatch(setEvalAbortController({ controller, type: GENIE_EVALUATE_TYPE.ALTERNATE }));

    try {
      const response = await sendMessagesEvaluation({ messages, commonData, signal: controller.signal });

      dispatch(updateEvaluatedPredictedMessages(response));
      dispatch(setEvalAbortController({ controller: null, type: GENIE_EVALUATE_TYPE.ALTERNATE }));
    } catch (error) {
      dispatch(cancelEvaluationByType(GENIE_EVALUATE_TYPE.ALTERNATE));

      if (controller.signal.aborted) return rejectWithValue('Cancelled');
      else dispatch(somethingWrongNotification());
      return rejectWithValue(error.message);
    }
  },
);

export const evaluateGeneratedMessagesConsumer = createAsyncThunk(
  EVALUATE_GENERATED_MESSAGES,
  async ({ values, messages }, { rejectWithValue, dispatch }) => {
    const { url, audienceId } = values;
    const commonData = { url, ...(audienceId ? { audienceId } : {}) };

    const controller = new AbortController();
    dispatch(setEvalAbortController({ controller, type: GENIE_EVALUATE_TYPE.GENERATED }));

    try {
      const response = await sendMessagesEvaluation({
        messages,
        commonData,
        signal: controller.signal,
      });

      dispatch(updateEvaluatedGeneratedMessages(response));
      dispatch(setEvalAbortController({ controller: null, type: GENIE_EVALUATE_TYPE.GENERATED }));
    } catch (error) {
      dispatch(cancelEvaluationByType(GENIE_EVALUATE_TYPE.GENERATED));

      if (controller.signal.aborted) return rejectWithValue('Cancelled');
      else dispatch(somethingWrongNotification());
      return rejectWithValue(error.message);
    }
  },
);

export const getConsumerGenieAudiences = createAsyncThunk(
  GET_CONSUMER_GENIE_AUDIENCES,
  async (_, { rejectWithValue }) => {
    const response = await sendRequest(
      () =>
        consumerApi.post(`/api/consumer/genie/audiences`, null, {
          authShape: AUTH_SHAPE.FLAT,
        }),
      {
        onError: (e) => rejectWithValue(e),
      },
    );

    return response.data;
  },
);
